// Employer marketplace. Two things happen here:
//  1. "Products for your employees" — turn each product the company has access to ON or
//     OFF per product. OFF (the default for a new product) means employees never see it
//     on My Products; the credit-service /me endpoints filter disabled products out.
//  2. "Discover" — browse published partner products and request access; once the
//     platform activates the agreement, the product appears above ready to switch on.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import { Store, Building2, ArrowRight, CheckCircle2, Clock, ShieldCheck } from 'lucide-react';
import { MP_CATEGORIES, groupKeyFor, productBadge, isLending, summarize } from './categories';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Switch } from '@/features/shared/components/ui/switch';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { creditApi, type MarketplaceProduct, type TenantAgreement } from '@/features/credit/api/creditApi';
import type { CreditProduct } from '@/features/credit/types';
import { formatMoney } from '../payroll/lib/format';
import { resetMarketplaceVisible } from './useMarketplaceVisible';
import { EmployerInsightsCard } from './EmployerInsightsCard';
import { AddOnsCard } from './AddOnsCard';

const STATUS: Record<string, { label: string; variant: 'secondary' | 'warning' | 'info' | 'success' | 'danger' }> = {
  pending: { label: 'Requested · pending review', variant: 'warning' },
  employer_review: { label: 'Awaiting your approval', variant: 'info' },
  active: { label: 'Enabled', variant: 'success' },
  suspended: { label: 'Suspended', variant: 'secondary' },
  revoked: { label: 'Revoked', variant: 'danger' },
  declined: { label: 'Declined', variant: 'danger' },
};


export default function MarketplaceDiscoverPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<MarketplaceProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);

  // "Products for your employees" — the per-product on/off surface.
  const [catalogue, setCatalogue] = useState<CreditProduct[]>([]);
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [cfgLoading, setCfgLoading] = useState(true);

  // Offers awaiting the EMPLOYER's approval (the employer sign step): a partner/platform
  // proposed this product to your company — nothing is enabled until you approve.
  const [pendingAgreements, setPendingAgreements] = useState<TenantAgreement[]>([]);
  const [deciding, setDeciding] = useState<string | null>(null);

  const loadAgreements = () =>
    creditApi
      .listTenantAgreements('employer_review')
      .then(setPendingAgreements)
      .catch(() => setPendingAgreements([]));

  const decide = async (a: TenantAgreement, approve: boolean) => {
    setDeciding(a.id);
    try {
      if (approve) await creditApi.approveTenantAgreement(a.id);
      else await creditApi.declineTenantAgreement(a.id, 'Declined by employer');
      toast.success(approve ? `${a.productName} approved — you can now switch it on for employees.` : `${a.productName} declined.`);
      await Promise.all([loadAgreements(), load(), loadConfig()]);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not record your decision.'));
    } finally {
      setDeciding(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      setProducts(await creditApi.marketplaceProducts());
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not load the marketplace'));
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    setCfgLoading(true);
    try {
      const [cat, programs] = await Promise.all([
        creditApi.listCatalogue().catch(() => [] as CreditProduct[]),
        creditApi.listPrograms().catch(() => []),
      ]);
      setCatalogue(cat);
      setEnabled(Object.fromEntries(programs.map((p) => [p.productId, p.enabled])));
    } finally {
      setCfgLoading(false);
    }
  };

  useEffect(() => {
    void load();
    void loadConfig();
    void loadAgreements();
  }, []);

  const request = async (p: MarketplaceProduct) => {
    setActing(p.id);
    try {
      await creditApi.requestAccess(p.id);
      toast.success(`Access requested for ${p.name}. We'll review and enable it shortly.`);
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not request access. Please try again.'), { duration: 6000 });
    } finally {
      setActing(null);
    }
  };

  const toggle = async (p: CreditProduct, next: boolean) => {
    setEnabled((m) => ({ ...m, [p.id]: next })); // optimistic
    setSavingId(p.id);
    try {
      await creditApi.setProgramEnabled(p.id, next);
      resetMarketplaceVisible(); // employees' My Products visibility recomputes
      toast.success(next ? `${p.name} is now visible to employees.` : `${p.name} is hidden from employees.`);
    } catch (e: any) {
      setEnabled((m) => ({ ...m, [p.id]: !next })); // revert
      toast.error(getApiErrorMessage(e, 'Could not update. HR or admin access is required.'), { duration: 6000 });
    } finally {
      setSavingId(null);
    }
  };

  const groups = useMemo(
    () =>
      MP_CATEGORIES
        .map((c) => ({ ...c, items: catalogue.filter((p) => groupKeyFor(p) === c.key) }))
        .filter((g) => g.items.length > 0),
    [catalogue],
  );

  const enabledCount = useMemo(
    () => catalogue.filter((p) => enabled[p.id]).length,
    [catalogue, enabled],
  );

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-6">
      <EmployerInsightsCard />
      <AddOnsCard />
      <header className="flex items-start gap-3">
        <div className="rounded-xl bg-primary-500/10 p-2.5 text-primary-500"><Store className="h-6 w-6" /></div>
        <div>
          <h1 className="text-xl font-semibold text-foreground">Marketplace</h1>
          <p className="mt-0.5 max-w-2xl text-sm text-muted-foreground">
            Choose which financial products your employees can see and apply for. Switch a product on to make it
            available on their My Products page; switch it off to hide it. New products start off.
          </p>
        </div>
      </header>

      {/* 0. Offers awaiting the employer's approval (the employer sign step) */}
      {pendingAgreements.length > 0 && (
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-warning" />
              <h2 className="font-semibold text-foreground">Awaiting your approval</h2>
              <span className="text-sm text-muted-foreground">— a partner proposed these products to your company. Nothing is shared or enabled until you approve.</span>
            </div>
            <div className="space-y-2">
              {pendingAgreements.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3">
                  <div className="min-w-0">
                    <span className="font-medium text-foreground">{a.productName || 'Partner product'}</span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Approving makes this product available to switch on for your employees — it stays off until you enable it above.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="text-danger hover:bg-danger-soft" disabled={deciding === a.id} onClick={() => decide(a, false)}>
                      Decline
                    </Button>
                    <Button size="sm" className="gap-1.5" disabled={deciding === a.id} onClick={() => decide(a, true)}>
                      <CheckCircle2 className="h-4 w-4" />
                      {deciding === a.id ? 'Saving…' : 'Approve'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 1. Products your company can turn on/off */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="font-semibold text-foreground">Products for your employees</h2>
              <p className="text-sm text-muted-foreground">
                Each product is off by default. Only products switched on appear on employees&rsquo; My Products.
              </p>
            </div>
            <Badge variant={enabledCount > 0 ? 'success' : 'secondary'}>
              {enabledCount} on
            </Badge>
          </div>

          {cfgLoading && <Skeleton className="h-24 w-full" />}
          {!cfgLoading && catalogue.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No products are available to your company yet. Discover and request access below.
            </p>
          )}

          {!cfgLoading && groups.map((g) => (
            <div key={g.key} className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <g.icon className="h-3.5 w-3.5" /> {g.label}
              </div>
              {g.items.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-foreground">{p.name}</span>
                      <Badge variant={productBadge(p).variant} className="shrink-0">
                        {productBadge(p).label}
                      </Badge>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">{summarize(p)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{enabled[p.id] ? 'Visible' : 'Hidden'}</span>
                    <Switch
                      checked={!!enabled[p.id]}
                      disabled={savingId === p.id}
                      onCheckedChange={(v) => toggle(p, v)}
                      aria-label={`Show ${p.name} to employees`}
                    />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* 2. Discover more partner products */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-semibold text-foreground">Discover more</h2>
          <span className="text-sm text-muted-foreground">— request access to partner products, then switch them on above.</span>
        </div>

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-52 rounded-xl" />)}
          </div>
        ) : products.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">
            No partner products are available yet. Check back soon.
          </CardContent></Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => {
              const st = p.agreementStatus ? STATUS[p.agreementStatus] : null;
              const canRequest = !p.agreementStatus || p.agreementStatus === 'declined' || p.agreementStatus === 'revoked';
              return (
                <Card key={p.id} className="flex flex-col">
                  <CardContent className="flex flex-1 flex-col gap-3 p-5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2 text-xs font-medium text-muted-foreground">
                        <Building2 className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{p.partnerName}</span>
                      </div>
                      <Badge variant={productBadge(p).variant} className="shrink-0">{productBadge(p).label}</Badge>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground">{p.name}</h3>
                      <p className="text-xs text-muted-foreground">{summarize(p)}</p>
                    </div>
                    {isLending(p) ? (
                      <dl className="mt-1 grid grid-cols-2 gap-2 text-xs">
                        <div><dt className="text-muted-foreground">Amount</dt>
                          <dd className="font-medium text-foreground">{formatMoney(p.minAmount, p.currency)}–{formatMoney(p.maxAmount, p.currency)}</dd></div>
                        <div><dt className="text-muted-foreground">Max term</dt>
                          <dd className="font-medium text-foreground">{p.maxTermMonths} months</dd></div>
                      </dl>
                    ) : p.maxAmount > 0 ? (
                      <dl className="mt-1 grid grid-cols-2 gap-2 text-xs">
                        <div><dt className="text-muted-foreground">Coverage</dt>
                          <dd className="font-medium text-foreground">up to {formatMoney(p.maxAmount, p.currency)}</dd></div>
                        <div><dt className="text-muted-foreground">Provided by</dt>
                          <dd className="font-medium text-foreground truncate">{p.partnerName}</dd></div>
                      </dl>
                    ) : (
                      <p className="mt-1 text-xs text-muted-foreground">Contact the partner for pricing and cover details.</p>
                    )}
                    <div className="mt-auto space-y-2 pt-2">
                      {st && (
                        <Badge variant={st.variant} className="gap-1">
                          {p.agreementStatus === 'active' ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                          {st.label}
                        </Badge>
                      )}
                      <Button size="sm" className="w-full gap-1" onClick={() => navigate(`products/${p.id}`, { relative: 'path' })}>
                        View details <ArrowRight className="h-4 w-4" />
                      </Button>
                      {canRequest && (
                        <Button size="sm" variant="outline" className="w-full" disabled={acting === p.id} onClick={() => request(p)}>
                          {acting === p.id ? 'Requesting…' : 'Request access'}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
